import React, { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import clsx from 'clsx';

export type AnalyticsChartHeaderPill = {
	label: string;
	color: string;
	bgColor: string;
	maxWidth?: number;
};

export type AnalyticsChartHeaderProps = {
	/** Optional pill rendered before the label (e.g., priority chip). */
	pill?: AnalyticsChartHeaderPill | null;
	/** Main label text (e.g., "Avg Approval Time", "Spend", "WO Performance"). */
	label: string;
	/**
	 * Already-formatted summary value displayed after the label (e.g., "1d 5h", "$12,345").
	 * When provided the header shows "{label}: {value}". When omitted only the label is shown.
	 */
	value?: string;
	/** Hover-tooltip text for the star info marker. When omitted the star is not rendered. */
	infoTooltip?: string;
	/** Optional delta indicator (arrow + percent). Rendered to the right of the value. */
	delta?: ReactNode;
	/** Tooltip shown when hovering the label/value group. Defaults to "{label}: {value}". */
	titleTooltip?: string;
	/** Optional content rendered after the label/value block (e.g., a filter chip). */
	after?: ReactNode;
	/** When false, removes flex-col from the wrapper so pill and label sit on the same row. Defaults to true. */
	flexCol?: boolean;
};

const AnalyticsChartHeader = ({
	pill,
	label,
	value,
	infoTooltip,
	delta,
	titleTooltip,
	after,
	flexCol = true,
}: AnalyticsChartHeaderProps) => {
	const labelTooltip = titleTooltip || (value ? `${label}: ${value}` : '');
	const labelBlock = (
		<div className="flex min-w-0 items-baseline gap-1">
			<span className="h5medium text-character-title/85 min-w-0 truncate">
				{value ? `${label}:` : label}
			</span>
			{value ? (
				<span className="h5medium text-character-title/85 shrink-0 whitespace-nowrap">{value}</span>
			) : null}
			{infoTooltip ? (
				<Tooltip title={infoTooltip}>
					<span className="relative inline-flex shrink-0 self-center leading-none">
						<InfoCircleOutlined style={{ fontSize: 12 }} />
					</span>
				</Tooltip>
			) : null}
			{delta ? <span className="shrink-0">{delta}</span> : null}
		</div>
	);
	return (
		<div className={`flex min-w-0 gap-2${flexCol ? ' flex-col' : ' flex-row items-center'}`}>
			{pill ? (
				<div className="inline-flex min-w-0 max-w-full self-start">
					<Tooltip title={pill.label}>
						<span
							className={clsx(
								'inline-block min-w-0 truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]',
								pill.maxWidth ? null : 'max-w-[140px]'
							)}
							style={{
								color: pill.color,
								backgroundColor: pill.bgColor,
								...(pill.maxWidth ? { maxWidth: pill.maxWidth } : {}),
							}}
						>
							{pill.label}
						</span>
					</Tooltip>
				</div>
			) : null}
			<div className="flex min-w-0 flex-nowrap items-center gap-3">
				{labelTooltip ? <Tooltip title={labelTooltip}>{labelBlock}</Tooltip> : labelBlock}
				{after}
			</div>
		</div>
	);
};

export default AnalyticsChartHeader;
